/**
 * Page-seed READs for `/settings/agents/*` — #44573.
 *
 * The Next originals call config helpers in-process from RSC. The Vite host
 * cannot do that; each page self-fetches through these GETs, then posts through
 * the already-mounted write contracts (agent-settings, project-actions,
 * other-actions, optimization-settings, verdicts, monitor, …).
 *
 * Live Next pages stay on their in-process reads (D6 — do not repoint).
 */

import { z } from "zod";
import type { Operation } from "../types";

// ── auto-verify (full config; the existing autoVerify.config is enabled+mode) ─

const autoVerifyPage = z.object({
  enabled: z.boolean(),
  mode: z.enum(["draft", "autonomous"]),
  model: z.string(),
  max_turns: z.number(),
  max_runtime_seconds: z.number(),
  dangerous_skip_permissions: z.boolean(),
  allow_subtasks: z.boolean(),
  max_subtasks: z.number(),
  limits: z.object({
    max_turns: z.object({ min: z.number(), max: z.number() }),
    max_runtime_seconds: z.object({ min: z.number(), max: z.number() }),
    max_subtasks: z.object({ min: z.number(), max: z.number() }),
  }),
});
export type AutoVerifyPageResponse = z.infer<typeof autoVerifyPage>;

const autoVerifyPageOp: Operation<void, void, void, AutoVerifyPageResponse> = {
  method: "GET",
  path: () => "/api/settings/agents/pages/auto-verify",
  response: autoVerifyPage,
};

// ── audits ──────────────────────────────────────────────────────────────────

const auditsPage = z.object({
  enabled: z.boolean(),
  cli: z.enum(["claude", "codex"]),
  model: z.string(),
  max_runtime_seconds: z.number(),
  default_cadence_days: z.number(),
  dangerous_skip_permissions: z.boolean(),
  automation: z.object({
    reconcile: z.boolean(),
    autorun: z.boolean(),
    autofix: z.boolean(),
  }),
  limits: z.object({
    max_runtime_seconds: z.object({ min: z.number(), max: z.number() }),
    default_cadence_days: z.object({ min: z.number(), max: z.number() }),
  }),
});
export type AuditsPageResponse = z.infer<typeof auditsPage>;

const auditsPageOp: Operation<void, void, void, AuditsPageResponse> = {
  method: "GET",
  path: () => "/api/settings/agents/pages/audits",
  response: auditsPage,
};

// ── autopilot ───────────────────────────────────────────────────────────────

const autopilotRole = z.object({
  name: z.string(),
  description: z.string(),
  enabled: z.boolean(),
  minutes: z.number(),
  inProfile: z.boolean(),
});
const autopilotPage = z.object({
  masterEnabled: z.boolean(),
  profileState: z.string(),
  minMinutes: z.number(),
  goalReconcileEnabled: z.boolean(),
  roles: z.array(autopilotRole),
});
export type AutopilotPageResponse = z.infer<typeof autopilotPage>;

const autopilotPageOp: Operation<void, void, void, AutopilotPageResponse> = {
  method: "GET",
  path: () => "/api/settings/agents/pages/autopilot",
  response: autopilotPage,
};

// ── clis / coding agents ────────────────────────────────────────────────────

const clisPage = z.object({
  config: z.record(z.string(), z.unknown()),
  agents: z.array(
    z.object({
      id: z.string(),
      label: z.string(),
      installed: z.boolean(),
      authed: z.boolean(),
      authorized: z.boolean(),
      disabled: z.boolean(),
      billing: z.string(),
      binaryPath: z.string().nullable(),
      experimental: z.boolean(),
    }),
  ),
  seed: z
    .object({
      defaultAgent: z.string(),
      chain: z.array(z.string()),
      reason: z.string(),
      needsInstallGuide: z.boolean(),
    })
    .nullable(),
  hasUserSeed: z.boolean(),
  seedJustWritten: z.boolean(),
  clis: z.array(
    z.object({
      id: z.string(),
      label: z.string(),
      installed: z.boolean(),
      mcpRegistered: z.boolean(),
      readinessState: z.enum(["linked", "installed", "not-installed"]),
      readinessReason: z.string(),
      restartRequiredAfterLink: z.boolean(),
      installCmd: z.string().nullable(),
      docs: z.string().nullable(),
    }),
  ),
  connected: z.number(),
});
export type ClisPageResponse = z.infer<typeof clisPage>;

const clisPageOp: Operation<void, void, void, ClisPageResponse> = {
  method: "GET",
  path: () => "/api/settings/agents/pages/clis",
  response: clisPage,
};

// ── consent ─────────────────────────────────────────────────────────────────

const consentPage = z.object({
  enabled: z.boolean(),
  autonomy: z.enum(["ask", "auto"]),
  sites: z.array(
    z.object({
      origin: z.string(),
      grantedAt: z.string().nullable().optional(),
      label: z.string().optional(),
    }),
  ),
});
export type ConsentPageResponse = z.infer<typeof consentPage>;

const consentPageOp: Operation<void, void, void, ConsentPageResponse> = {
  method: "GET",
  path: () => "/api/settings/agents/pages/consent",
  response: consentPage,
};

// ── copilot ─────────────────────────────────────────────────────────────────

const copilotPage = z.object({
  enabled: z.boolean(),
  develop: z.boolean(),
  autonomy: z.enum(["ask", "auto"]),
  warmSessions: z.boolean(),
});
export type CopilotPageResponse = z.infer<typeof copilotPage>;

const copilotPageOp: Operation<void, void, void, CopilotPageResponse> = {
  method: "GET",
  path: () => "/api/settings/agents/pages/copilot",
  response: copilotPage,
};

// ── credentials ─────────────────────────────────────────────────────────────

const credentialsPage = z.object({
  rows: z.array(
    z.object({
      provider: z.string(),
      label: z.string().optional(),
      provenance: z.string(),
      detail: z.string().optional(),
    }),
  ),
});
export type CredentialsPageResponse = z.infer<typeof credentialsPage>;

const credentialsPageOp: Operation<void, void, void, CredentialsPageResponse> = {
  method: "GET",
  path: () => "/api/settings/agents/pages/credentials",
  response: credentialsPage,
};

// ── fleet ───────────────────────────────────────────────────────────────────

const fleetPage = z.object({
  enabled: z.boolean(),
  poolSize: z.number(),
  enabledEnvPinned: z.boolean(),
  poolSizeEnvPinned: z.boolean(),
  defaultPool: z.number(),
  min: z.number(),
  max: z.number(),
  pool: z.object({
    active: z.number().optional(),
    idle: z.number().optional(),
    total: z.number().optional(),
    capacity: z.number().optional(),
  }).passthrough(),
});
export type FleetPageResponse = z.infer<typeof fleetPage>;

const fleetPageOp: Operation<void, void, void, FleetPageResponse> = {
  method: "GET",
  path: () => "/api/settings/agents/pages/fleet",
  response: fleetPage,
};

// ── inbound ─────────────────────────────────────────────────────────────────

const inboundPage = z.object({
  enabled: z.boolean(),
  rateLimitPerHour: z.number().optional(),
  secrets: z.object({
    telegramBotToken: z.boolean().optional(),
  }).passthrough(),
  pending: z
    .object({
      code: z.string().optional(),
      expiresAt: z.string().optional(),
    })
    .nullable()
    .optional(),
});
export type InboundPageResponse = z.infer<typeof inboundPage>;

const inboundPageOp: Operation<void, void, void, InboundPageResponse> = {
  method: "GET",
  path: () => "/api/settings/agents/pages/inbound",
  response: inboundPage,
};

// ── learning ────────────────────────────────────────────────────────────────

const learningRow = z.object({
  id: z.number(),
  title: z.string(),
  body: z.string(),
  created_at: z.string(),
  last_used_at: z.string().nullable().optional(),
  pinned: z.boolean().optional(),
  blast: z.string().optional(),
});
const learningPage = z.object({
  config: z.object({
    enabled: z.boolean(),
    autoSkillTasks: z.boolean(),
    injectTopK: z.number().optional(),
  }),
  proposed: z.array(learningRow),
  saved: z.array(learningRow),
  reviewedSessions: z.number(),
  savedCount: z.number(),
});
export type LearningPageResponse = z.infer<typeof learningPage>;

const learningPageOp: Operation<void, void, void, LearningPageResponse> = {
  method: "GET",
  path: () => "/api/settings/agents/pages/learning",
  response: learningPage,
};

// ── mirror ──────────────────────────────────────────────────────────────────

const mirrorPage = z.object({
  enabled: z.boolean(),
  remote: z.string(),
  branch: z.string(),
  intervalSeconds: z.number().optional(),
});
export type MirrorPageResponse = z.infer<typeof mirrorPage>;

const mirrorPageOp: Operation<void, void, void, MirrorPageResponse> = {
  method: "GET",
  path: () => "/api/settings/agents/pages/mirror",
  response: mirrorPage,
};

// ── models/residency ────────────────────────────────────────────────────────

const residencyPage = z.object({
  sovereign: z.boolean(),
  local: z.array(
    z.object({
      id: z.string(),
      baseUrl: z.string().optional(),
    }),
  ),
  remote: z.array(
    z.object({
      id: z.string(),
      baseUrl: z.string().optional(),
      transport: z.string().optional(),
      dataPolicy: z.string().nullable().optional(),
      consented: z.boolean(),
    }),
  ),
  consented: z.array(z.string()),
});
export type ResidencyPageResponse = z.infer<typeof residencyPage>;

const residencyPageOp: Operation<void, void, void, ResidencyPageResponse> = {
  method: "GET",
  path: () => "/api/settings/agents/pages/residency",
  response: residencyPage,
};

// ── notify ──────────────────────────────────────────────────────────────────

const notifyPage = z.object({
  enabled: z.boolean(),
  provider: z.enum(["log", "resend", "telegram", "http"]),
  from: z.string().optional(),
  events: z.object({
    reviewQueue: z.boolean(),
    promoteReady: z.boolean(),
    autopilotStall: z.boolean(),
    auditHigh: z.boolean(),
    inboundAck: z.boolean(),
  }).passthrough(),
  digest: z.enum(["instant", "daily"]).optional(),
  quietHoursStart: z.number().optional(),
  quietHoursEnd: z.number().optional(),
  recipient: z.string().optional(),
  secrets: z.record(z.string(), z.boolean()).optional(),
});
export type NotifyPageResponse = z.infer<typeof notifyPage>;

const notifyPageOp: Operation<void, void, void, NotifyPageResponse> = {
  method: "GET",
  path: () => "/api/settings/agents/pages/notify",
  response: notifyPage,
};

// ── optimization ────────────────────────────────────────────────────────────

const optimizationPage = z.object({
  config: z.record(z.string(), z.unknown()),
  compileAutorun: z.record(z.string(), z.unknown()),
  consumedTokensToday: z.number(),
  consumedUsdToday: z.number(),
});
export type OptimizationPageResponse = z.infer<typeof optimizationPage>;

const optimizationPageOp: Operation<void, void, void, OptimizationPageResponse> = {
  method: "GET",
  path: () => "/api/settings/agents/pages/optimization",
  response: optimizationPage,
};

// ── telemetry ───────────────────────────────────────────────────────────────

const telemetryPage = z.object({
  optedIn: z.boolean(),
  publicOptedIn: z.boolean(),
  publicMode: z.boolean(),
  sinkProvider: z.string(),
  sinkConfigured: z.boolean(),
  sinkDestination: z.string().nullable(),
  hasApiKey: z.boolean(),
  disclosure: z.record(z.string(), z.unknown()).optional(),
});
export type TelemetryPageResponse = z.infer<typeof telemetryPage>;

const telemetryPageOp: Operation<void, void, void, TelemetryPageResponse> = {
  method: "GET",
  path: () => "/api/settings/agents/pages/telemetry",
  response: telemetryPage,
};

// ── verdicts routing ────────────────────────────────────────────────────────

const verdictsPage = z.object({
  routing: z.record(z.string(), z.string().nullable()),
  approvers: z.array(
    z.object({
      external_id: z.string(),
      label: z.string(),
    }),
  ),
  currentLabel: z.string(),
  departments: z.array(z.string()).optional(),
});
export type VerdictsPageResponse = z.infer<typeof verdictsPage>;

const verdictsPageOp: Operation<void, void, void, VerdictsPageResponse> = {
  method: "GET",
  path: () => "/api/settings/agents/pages/verdicts",
  response: verdictsPage,
};

// ── monitor (seed; writes already on monitor.*) ─────────────────────────────

const monitorPage = z.object({
  enabled: z.boolean(),
  watch: z.record(z.string(), z.unknown()),
  status: z.object({
    running: z.boolean(),
    browser: z.string().nullable(),
    profileDir: z.string().optional(),
    chromeBin: z.string().nullable().optional(),
    systemProfileAvailable: z.boolean().optional(),
    systemProfileBusy: z.boolean().optional(),
  }).passthrough(),
  suggestedKeywords: z.array(z.string()),
  opportunities: z.array(z.record(z.string(), z.unknown())),
  extension: z.record(z.string(), z.unknown()).optional(),
  extensionId: z.string().optional(),
});
export type MonitorPageResponse = z.infer<typeof monitorPage>;

const monitorPageOp: Operation<void, void, void, MonitorPageResponse> = {
  method: "GET",
  path: () => "/api/settings/agents/pages/monitor",
  response: monitorPage,
};

// ── integrate (seed) ────────────────────────────────────────────────────────

const integratePage = z.object({
  enabled: z.boolean(),
  acknowledged: z.boolean(),
  profile: z.string().optional(),
  status: z.record(z.string(), z.unknown()),
  tools: z.array(z.record(z.string(), z.unknown())),
  catalog: z.array(z.record(z.string(), z.unknown())),
  extension: z.record(z.string(), z.unknown()).optional(),
  extensionId: z.string().optional(),
});
export type IntegratePageResponse = z.infer<typeof integratePage>;

const integratePageOp: Operation<void, void, void, IntegratePageResponse> = {
  method: "GET",
  path: () => "/api/settings/agents/pages/integrate",
  response: integratePage,
};

// ── repos is already reposList; thin marker for dual-host probe ──────────────

const reposPage = z.object({ ok: z.literal(true) });
const reposPageOp: Operation<void, void, void, z.infer<typeof reposPage>> = {
  method: "GET",
  path: () => "/api/settings/agents/pages/repos",
  response: reposPage,
};

// ── barrel ──────────────────────────────────────────────────────────────────

export const settingsAgentsPages = {
  autoVerify: autoVerifyPageOp,
  audits: auditsPageOp,
  autopilot: autopilotPageOp,
  clis: clisPageOp,
  consent: consentPageOp,
  copilot: copilotPageOp,
  credentials: credentialsPageOp,
  fleet: fleetPageOp,
  inbound: inboundPageOp,
  learning: learningPageOp,
  mirror: mirrorPageOp,
  residency: residencyPageOp,
  notify: notifyPageOp,
  optimization: optimizationPageOp,
  telemetry: telemetryPageOp,
  verdicts: verdictsPageOp,
  monitor: monitorPageOp,
  integrate: integratePageOp,
  repos: reposPageOp,
};

export const SETTINGS_AGENTS_PAGE_OPS = Object.values(settingsAgentsPages);
